import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Package, Loader2, AlertTriangle, CheckCircle, RefreshCw, Plus, Minus,
  Download, FileSpreadsheet, History, RotateCcw, FileText, Calendar
} from 'lucide-react';
import { inventoryAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { escapeHtml } from '../utils/escapeHtml';
import DOMPurify from 'dompurify';

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [usageReport, setUsageReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('inventory');
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [selectedItemLogs, setSelectedItemLogs] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');
  const { toast } = useToast();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await inventoryAPI.initialize();
      const [invData, alertsData, logsData] = await Promise.all([
        inventoryAPI.getAll(),
        inventoryAPI.getAlerts(),
        inventoryAPI.getLogs(null, 100)
      ]);
      setInventory(invData);
      setAlerts(alertsData);
      setLogs(logsData);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error loading inventory', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageReport = async () => {
    try {
      const report = await inventoryAPI.getUsageReport();
      setUsageReport(report);
    } catch (error) {
      toast({ title: 'Error loading usage report', variant: 'destructive' });
    }
  };

  const updateQuantity = async (item, delta) => {
    const newQty = Math.max(0, item.available_quantity + delta);
    try {
      await inventoryAPI.update(item.equipment_id, { 
        available_quantity: newQty,
        total_quantity: delta > 0 ? item.total_quantity + delta : item.total_quantity
      });
      toast({ title: 'Inventory updated!' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating inventory', variant: 'destructive' });
    }
  };

  const updateThreshold = async (item, threshold) => {
    try {
      await inventoryAPI.update(item.equipment_id, { low_stock_threshold: parseInt(threshold) });
      toast({ title: 'Threshold updated!' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating threshold', variant: 'destructive' });
    }
  };

  const handleRestock = async (item) => {
    if (!window.confirm(`Reset "${item.equipment_name}" to full stock (${item.total_quantity} units)?`)) return;
    try {
      await inventoryAPI.restock(item.equipment_id);
      toast({ title: 'Item restocked!' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error restocking item', variant: 'destructive' });
    }
  };

  const showItemHistory = async (item) => {
    try {
      const itemLogs = await inventoryAPI.getLogs(item.equipment_id, 50);
      setSelectedItemLogs(itemLogs);
      setSelectedItemName(item.equipment_name);
      setShowLogsDialog(true);
    } catch (error) {
      toast({ title: 'Error loading history', variant: 'destructive' });
    }
  };

  const exportToCSV = async () => {
    try {
      const data = await inventoryAPI.exportInventory();
      
      const headers = ['Name', 'Category', 'Total Qty', 'Available', 'Reserved', 'Threshold', 'Status'];
      const rows = data.items.map(item => [
        item.equipment_name,
        item.category,
        item.total_quantity,
        item.available_quantity,
        item.reserved_quantity,
        item.low_stock_threshold,
        item.available_quantity <= item.low_stock_threshold ? 'LOW STOCK' : 'OK'
      ]);

      // Add summary
      rows.push([]);
      rows.push(['SUMMARY']);
      rows.push(['Total Items', data.summary.total_items]);
      rows.push(['Total Quantity', data.summary.total_quantity]);
      rows.push(['Available', data.summary.available_quantity]);
      rows.push(['Reserved', data.summary.reserved_quantity]);
      rows.push(['Low Stock Items', data.summary.low_stock_count]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `inventory_report_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast({ title: 'CSV exported successfully!' });
    } catch (error) {
      toast({ title: 'Error exporting CSV', variant: 'destructive' });
    }
  };

  const exportToPDF = async () => {
    try {
      const data = await inventoryAPI.exportInventory();
      const html2pdf = (await import('html2pdf.js')).default;
      
      const reportContent = document.createElement('div');
      reportContent.innerHTML = DOMPurify.sanitize(`
        <div style="font-family: Arial, sans-serif; padding: 40px; max-width: 800px;">
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e3a5f; padding-bottom: 20px;">
            <h1 style="color: #1e3a5f; margin: 0; font-size: 28px;">Inventory Report</h1>
            <p style="color: #666; margin-top: 10px;">Generated: ${new Date().toLocaleDateString()}</p>
          </div>

          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px;">
            <div style="background: #f0fdf4; padding: 15px; border-radius: 8px; text-align: center;">
              <p style="margin: 0; color: #666; font-size: 12px;">Total Items</p>
              <p style="margin: 5px 0 0; font-size: 24px; font-weight: bold; color: #1e3a5f;">${data.summary.total_items}</p>
            </div>
            <div style="background: #eff6ff; padding: 15px; border-radius: 8px; text-align: center;">
              <p style="margin: 0; color: #666; font-size: 12px;">Available</p>
              <p style="margin: 5px 0 0; font-size: 24px; font-weight: bold; color: #1e3a5f;">${data.summary.available_quantity}</p>
            </div>
            <div style="background: #faf5ff; padding: 15px; border-radius: 8px; text-align: center;">
              <p style="margin: 0; color: #666; font-size: 12px;">Reserved</p>
              <p style="margin: 5px 0 0; font-size: 24px; font-weight: bold; color: #1e3a5f;">${data.summary.reserved_quantity}</p>
            </div>
            <div style="background: ${data.summary.low_stock_count > 0 ? '#fef2f2' : '#f0fdf4'}; padding: 15px; border-radius: 8px; text-align: center;">
              <p style="margin: 0; color: #666; font-size: 12px;">Low Stock</p>
              <p style="margin: 5px 0 0; font-size: 24px; font-weight: bold; color: ${data.summary.low_stock_count > 0 ? '#dc2626' : '#16a34a'};">${data.summary.low_stock_count}</p>
            </div>
          </div>

          ${data.summary.low_stock_count > 0 ? `
            <div style="background: #fef2f2; border: 1px solid #fecaca; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <h3 style="color: #dc2626; margin: 0 0 10px 0; font-size: 14px;">⚠️ Low Stock Alerts</h3>
              <p style="margin: 0; color: #7f1d1d;">${escapeHtml(data.summary.low_stock_items.join(', '))}</p>
            </div>
          ` : ''}

          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <thead>
              <tr style="background: #f5f5dc;">
                <th style="padding: 12px; text-align: left; border-bottom: 2px solid #1e3a5f;">Item</th>
                <th style="padding: 12px; text-align: center; border-bottom: 2px solid #1e3a5f;">Category</th>
                <th style="padding: 12px; text-align: center; border-bottom: 2px solid #1e3a5f;">Available</th>
                <th style="padding: 12px; text-align: center; border-bottom: 2px solid #1e3a5f;">Reserved</th>
                <th style="padding: 12px; text-align: center; border-bottom: 2px solid #1e3a5f;">Total</th>
                <th style="padding: 12px; text-align: center; border-bottom: 2px solid #1e3a5f;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${data.items.map(item => `
                <tr>
                  <td style="padding: 10px; border-bottom: 1px solid #eee;">${escapeHtml(item.equipment_name)}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center; text-transform: capitalize;">${escapeHtml(item.category?.replace('_', ' '))}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.available_quantity}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.reserved_quantity}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.total_quantity}</td>
                  <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">
                    <span style="padding: 4px 8px; border-radius: 12px; font-size: 12px; ${item.available_quantity <= item.low_stock_threshold ? 'background: #fef2f2; color: #dc2626;' : 'background: #f0fdf4; color: #16a34a;'}">
                      ${item.available_quantity <= item.low_stock_threshold ? 'LOW' : 'OK'}
                    </span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div style="margin-top: 40px; text-align: center; color: #999; font-size: 12px;">
            <p>Willow Ridge Catering - Inventory Management System</p>
          </div>
        </div>
      `);

      const opt = {
        margin: 10,
        filename: `inventory_report_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(reportContent).save();
      toast({ title: 'PDF exported successfully!' });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({ title: 'Error exporting PDF', variant: 'destructive' });
    }
  };

  const filteredInventory = categoryFilter === 'all' 
    ? inventory 
    : inventory.filter(i => i.category === categoryFilter);

  const categories = [...new Set(inventory.map(i => i.category))];
  const isLowStock = (item) => item.available_quantity <= item.low_stock_threshold;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getActionColor = (action) => {
    const colors = {
      'checkout': 'bg-red-100 text-red-800',
      'return': 'bg-green-100 text-green-800',
      'reserve': 'bg-blue-100 text-blue-800',
      'release': 'bg-purple-100 text-purple-800',
      'restock': 'bg-amber-100 text-amber-800',
      'adjust': 'bg-gray-100 text-gray-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
      <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Inventory Tracking</h1>
          <p className="text-gray-600">Monitor stock levels and manage equipment</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]" data-testid="export-csv-btn">
            <FileSpreadsheet size={18} className="mr-2" />CSV
          </Button>
          <Button onClick={exportToPDF} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]" data-testid="export-pdf-btn">
            <Download size={18} className="mr-2" />PDF
          </Button>
          <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
            <RefreshCw size={20} className="mr-2" />Refresh
          </Button>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Card className="border-none shadow-lg mb-8 bg-red-50 border-l-4 border-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={24} />Low Stock Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {alerts.map((alert) => (
                <div key={alert.id || alert.name} className="p-3 bg-white rounded-lg border border-red-200">
                  <p className="font-semibold text-sm text-[#1e3a5f]">{alert.name}</p>
                  <p className="text-red-600 text-xs">Only {alert.available} left (threshold: {alert.threshold})</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-none shadow-lg">
          <CardContent className="p-6 text-center">
            <Package className="mx-auto text-[#1e3a5f] mb-2" size={32} />
            <p className="text-3xl font-bold text-[#1e3a5f]">{inventory.length}</p>
            <p className="text-sm text-gray-600">Total Items</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-lg">
          <CardContent className="p-6 text-center">
            <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
            <p className="text-3xl font-bold text-green-600">{inventory.filter(i => !isLowStock(i)).length}</p>
            <p className="text-sm text-gray-600">In Stock</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-lg">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="mx-auto text-red-500 mb-2" size={32} />
            <p className="text-3xl font-bold text-red-600">{alerts.length}</p>
            <p className="text-sm text-gray-600">Low Stock</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-lg">
          <CardContent className="p-6 text-center">
            <Package className="mx-auto text-blue-500 mb-2" size={32} />
            <p className="text-3xl font-bold text-blue-600">{inventory.reduce((sum, i) => sum + i.reserved_quantity, 0)}</p>
            <p className="text-sm text-gray-600">Reserved</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-white">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
            <Package size={16} className="mr-2" />Inventory
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
            <History size={16} className="mr-2" />History Log
          </TabsTrigger>
          <TabsTrigger value="usage" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white" onClick={fetchUsageReport}>
            <FileText size={16} className="mr-2" />Usage Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-6">
          {/* Category Filter */}
          <div className="mb-6">
            <Tabs value={categoryFilter} onValueChange={setCategoryFilter}>
              <TabsList className="bg-white flex-wrap">
                <TabsTrigger value="all" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">All</TabsTrigger>
                {categories.map(cat => (
                  <TabsTrigger key={cat} value={cat} className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white capitalize">
                    {cat?.replace('_', ' ')}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInventory.map((item) => (
              <Card key={item.id} className={`border-none shadow-lg ${isLowStock(item) ? 'ring-2 ring-red-400' : ''}`} data-testid={`inventory-${item.equipment_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg text-[#1e3a5f]">{item.equipment_name}</CardTitle>
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded capitalize">{item.category?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex gap-1">
                      {isLowStock(item) && <AlertTriangle className="text-red-500" size={20} />}
                      <Button variant="ghost" size="sm" onClick={() => showItemHistory(item)} className="h-8 w-8 p-0" data-testid={`history-${item.equipment_id}`}>
                        <History size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Available</span>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => updateQuantity(item, -1)} data-testid={`decrease-${item.equipment_id}`}>
                          <Minus size={14} />
                        </Button>
                        <span className={`text-xl font-bold min-w-[40px] text-center ${isLowStock(item) ? 'text-red-600' : 'text-[#1e3a5f]'}`}>
                          {item.available_quantity}
                        </span>
                        <Button size="sm" variant="outline" onClick={() => updateQuantity(item, 1)} data-testid={`increase-${item.equipment_id}`}>
                          <Plus size={14} />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-600 text-xs">Total</p>
                        <p className="font-semibold">{item.total_quantity}</p>
                      </div>
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="text-gray-600 text-xs">Reserved</p>
                        <p className="font-semibold text-blue-600">{item.reserved_quantity}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">Alert threshold:</span>
                      <Input 
                        type="number" 
                        min="0"
                        value={item.low_stock_threshold} 
                        onChange={(e) => updateThreshold(item, e.target.value)}
                        className="w-16 h-8 text-center text-sm"
                        data-testid={`threshold-${item.equipment_id}`}
                      />
                    </div>

                    {/* Quick Restock Button */}
                    {item.available_quantity < item.total_quantity && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full border-amber-500 text-amber-700 hover:bg-amber-50"
                        onClick={() => handleRestock(item)}
                        data-testid={`restock-${item.equipment_id}`}
                      >
                        <RotateCcw size={14} className="mr-2" />
                        Quick Restock
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredInventory.length === 0 && (
            <div className="text-center py-20">
              <Package className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500">No inventory items found</p>
              <p className="text-sm text-gray-400 mt-2">Add equipment on the Equipment page first</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <History size={20} />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {logs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No activity logs yet</p>
                ) : (
                  logs.map((log) => (
                    <div key={log.id || `${log.equipment_id}-${log.created_at}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`log-${log.id || log.created_at}`}>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                          {log.action.toUpperCase()}
                        </span>
                        <div>
                          <p className="font-medium text-[#1e3a5f]">{log.equipment_name}</p>
                          <p className="text-xs text-gray-500">
                            {log.event_name && `Event: ${log.event_name} • `}
                            {log.notes || `Qty: ${log.quantity_change > 0 ? '+' : ''}${log.quantity_change}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <p>{formatDate(log.created_at)}</p>
                        {log.contract_id && <p className="text-blue-600">Contract linked</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="mt-6">
          {usageReport ? (
            <div className="space-y-6">
              {/* By Equipment */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                    <Package size={20} />
                    Equipment Usage Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {usageReport.by_equipment?.map((eq, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-[#1e3a5f]">{eq.equipment_name}</p>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                          <div>
                            <p className="text-xs text-gray-500">Checkouts</p>
                            <p className="font-bold text-red-600">{eq.total_checkouts}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Returns</p>
                            <p className="font-bold text-green-600">{eq.total_returns}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Events</p>
                            <p className="font-bold text-blue-600">{eq.events_count}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(!usageReport.by_equipment || usageReport.by_equipment.length === 0) && (
                    <p className="text-gray-500 text-center py-8">No usage data available</p>
                  )}
                </CardContent>
              </Card>

              {/* By Contract */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                    <Calendar size={20} />
                    Equipment by Event
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {usageReport.by_contract?.map((contract, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-[#1e3a5f]">{contract.event_name || 'Unknown Event'}</p>
                          <span className="text-xs text-gray-500">{contract.items?.length} items</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {contract.items?.map((item, iIdx) => (
                            <span key={iIdx} className={`px-2 py-1 rounded text-xs ${getActionColor(item.action)}`}>
                              {item.equipment_name} ({item.quantity})
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(!usageReport.by_contract || usageReport.by_contract.length === 0) && (
                    <p className="text-gray-500 text-center py-8">No contract usage data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-20">
              <Loader2 className="animate-spin text-[#1e3a5f] mx-auto mb-4" size={32} />
              <p className="text-gray-500">Loading usage report...</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Item History Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>History - {selectedItemName}</DialogTitle>
            <DialogDescription>Activity log for this item</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selectedItemLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No history for this item</p>
            ) : (
              selectedItemLogs.map((log, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                      {log.action.toUpperCase()}
                    </span>
                    <div>
                      <p className="text-sm font-medium">
                        {log.quantity_change > 0 ? '+' : ''}{log.quantity_change} units
                      </p>
                      {log.notes && <p className="text-xs text-gray-500">{log.notes}</p>}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(log.created_at)}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
